import {
  getPaginationRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getCoreRowModel,
  PaginationState,
  useReactTable,
  SortingState,
} from '@ui/components';
import { getCostCentersPaginated } from '@api/cost-centers';
import { useState, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getTableRequest } from '@shared/utils';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@shared/hooks';
import { useDebounce } from '@ui/hooks';

import { getCostCentersColumns } from './cost-centers-columns';

export const useCostCentersTable = () => {
  const totalItemsRef = useRef<number>(0);
  const { currency, companyId } = useCurrentUser();
  const { t } = useTranslation();

  const columns = getCostCentersColumns(t, currency);

  const [tableSearchQuery, setTableSearchQuery] = useState('');
  const debouncedTableSearchQuery = useDebounce(tableSearchQuery, 800);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const [sorting, setSorting] = useState<SortingState>([]);

  const request = useMemo(
    () => getTableRequest({ pagination, sorting, search: debouncedTableSearchQuery }),
    [pagination, sorting, debouncedTableSearchQuery],
  );

  const { data: apiData, isLoading } = useQuery({
    queryKey: ['cost-centers', companyId, request],
    queryFn: () => getCostCentersPaginated(request),
  });

  const totalItems =
    apiData?.pagination?.totalItems !== undefined
      ? apiData.pagination.totalItems
      : totalItemsRef.current;

  const table = useReactTable({
    data: apiData?.data ?? [],
    columns: columns,
    state: { pagination, sorting, globalFilter: tableSearchQuery },
    pageCount: Math.ceil(totalItems / pagination.pageSize),
    rowCount: totalItems,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    getRowId: (row) => row.id as unknown as string,
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: setTableSearchQuery,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
  });

  return { table, totalItems, pagination, sorting, tableSearchQuery, t, isLoading };
};
